import React, { useEffect, useState } from 'react';
import styles from './client-table.module.css';
import { User, TableColumn } from '../../types';
import sortIcon from '../../assets/sort-icon.svg';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Table } from '../table';
import { MemberModal } from '../member-modal';
import { useFetchOrganisation, useFetchPlans, useFetchUsers } from '../../actions';
import { RootState } from '../../store/store';
import { StatusBadge, SortModal, SearchBar, Pagination } from '../table';
import { PlanType } from '../../constants';
import { DATE_FORMAT } from '../../constants';
import { useFetchCurrency } from '../../actions/currency';
import { useFetchAdmin } from '../../actions/admin';
import { useTranslation } from 'react-i18next';

export const ClientTable: React.FC = () => {
  const { t } = useTranslation();
  const [filteredUsers, setFilteredUsers] = useState<User[]>();
  const [user, setUser] = useState<User>();
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSortModalOpen, setIsSortModalOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [statusFilter, setStatusFilter] = useState<string>(
    () => localStorage.getItem('statusFilter') || 'active',
  );

  const { users, plans, organisation } = useSelector((state: RootState) => state.organization);
  const { rates } = useSelector((state: RootState) => state.rates);
  const { admin } = useSelector((state: RootState) => state.admin);

  const fetchUsers = useFetchUsers();
  const fetchPlans = useFetchPlans();

  const handleStatusFilterChange = (status: string) => {
    localStorage.removeItem('search');
    localStorage.setItem('statusFilter', status);
    setStatusFilter(status);
    setCurrentPage(1);
    fetchUsers();
  };
  const fetchOrganisation = useFetchOrganisation();
  const fetchRate = useFetchCurrency();
  const fetchAdmin = useFetchAdmin();

  useEffect(() => {
    if (users) {
      setFilteredUsers(users);
    }
  }, [users]);

  useEffect(() => {
    const fetchData = async () => {
      if (!admin) {
        await fetchAdmin();
      }
    };

    fetchData();
  }, [admin]);

  useEffect(() => {
    if (!rates && admin) {
      fetchRate();
    }
  }, [admin, rates]);

  useEffect(() => {
    if (!localStorage.getItem('statusFilter')) {
      localStorage.setItem('statusFilter', 'active');
    }
  }, []);

  useEffect(() => {
    if (admin && !users) {
      fetchUsers();
    }

    if (admin && !plans) {
      fetchPlans();
    }

    if (admin && !organisation) {
      fetchOrganisation();
    }
  }, [admin]);

  const getPagedUsers = () => {
    if (!filteredUsers) return [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  };
  const columns: TableColumn[] = [
    { header: t('id'), accessor: 'id', sortable: true },
    {
      header: t('fullName'),
      accessor: 'fullName',
      sortable: true,
      render: (row: User) =>
        [row.user?.info?.firstName, row.user?.info?.midName, row.user?.info?.lastName]
          .filter(Boolean)
          .join(' ') || '-',
    },
    {
      header: t('phone'),
      accessor: 'phone',
      sortable: true,
      render: (row: User) => row.user?.phone,
    },
    {
      header: t('planType'),
      accessor: 'planType',
      sortable: true,
      render: (row: User) => `${row.plan?.planType} ${row.plan?.insuranceType}`,
    },
    {
      header: t('planPrice'),
      accessor: 'price',
      sortable: true,
      render: (row: User) =>
        `${row.plan?.price / 100} ${plans?.find((i) => row.planId === i.plan?.id)?.plan?.currency}`,
    },
    {
      header: t('members'),
      accessor: 'protectedMembersAmount',
      sortable: false,
      render: (row: User) =>
        row.plan?.planType === PlanType.FAMILY
          ? `${row.user?.family?.length}/${row.plan?.protectedMembersAmount}`
          : 'N/A',
    },
    {
      header: t('status'),
      accessor: 'isEnabled',
      sortable: true,
      render: (row: User) => (
        <StatusBadge status={row.deletedAt ? 'Deleted' : row.isEnabled ? 'Active' : 'Inactive'} />
      ),
    },
    {
      header: t('onboarded'),
      accessor: 'isOnboarded',
      sortable: false,
      render: (row: User) => (row.user?.isOnboarded ? t('yes') : t('no')),
    },
    {
      header: t('createdDate'),
      accessor: 'createdAt',
      sortable: true,
      render: (row: User) => moment(row.createdAt).format(DATE_FORMAT),
    },
    {
      header: t('startedDate'),
      accessor: 'startAt',
      sortable: true,
      render: (row: User) => moment(row.startAt).format(DATE_FORMAT),
    },
    {
      header: t('endDate'),
      accessor: 'endAt',
      sortable: false,
      render: (row: User) => moment(row.endAt).format(DATE_FORMAT),
    },
    {
      header: t('deletedDate'),
      accessor: 'deletedAt',
      sortable: false,
      render: (row: User) => (row.deletedAt ? moment(row.deletedAt).format(DATE_FORMAT) : '-'),
    },
  ];

  const handleEdit = (row: User) => {
    setIsModalOpen(true);
    setUser(row);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className={styles.container}>
      <div className={styles['sortButtonContainer']}>
        <div className={styles.filtersRow}>
          <SearchBar
            key={statusFilter}
            setCurrentPage={setCurrentPage}
          />
          <div className={styles.statusFilter}>
            <button
              type="button"
              className={`${styles.statusButton} ${statusFilter === 'all' ? styles.statusButtonActive : ''}`}
              onClick={() => handleStatusFilterChange('all')}
            >
              {t('all')}
            </button>
            <button
              type="button"
              className={`${styles.statusButton} ${statusFilter === 'active' ? styles.statusButtonActive : ''}`}
              onClick={() => handleStatusFilterChange('active')}
            >
              {t('active')}
            </button>
            <button
              type="button"
              className={`${styles.statusButton} ${statusFilter === 'inactive' ? styles.statusButtonActive : ''}`}
              onClick={() => handleStatusFilterChange('inactive')}
            >
              {t('inactive')}
            </button>
            <button
              type="button"
              className={`${styles.statusButton} ${statusFilter === 'deleted' ? styles.statusButtonActive : ''}`}
              onClick={() => handleStatusFilterChange('deleted')}
            >
              {t('deleted')}
            </button>
          </div>
        </div>
        <button
          className={styles.sortButton}
          onClick={() => setIsSortModalOpen(true)}
        >
          <img
            src={sortIcon}
            alt="#"
          />{' '}
          {t('sort')}
        </button>
        {isSortModalOpen && (
          <SortModal
            columns={columns}
            onClose={() => setIsSortModalOpen(false)}
          />
        )}
      </div>
      <Table
        data={getPagedUsers()}
        columns={columns}
        onEdit={handleEdit}
      />
      <Pagination
        totalItems={users?.length ? users.length : 0}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
      <MemberModal
        isOpen={isModalOpen}
        onClose={() => {
          setCurrentPage(1);
          setIsModalOpen(false);
        }}
        client={user}
        admin={admin}
      />
    </div>
  );
};
